import { motion } from 'framer-motion';
import { Layout, PencilLine, Sprout, ChevronRight, X, Sparkles, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChoiceOptionProps {
  title: string;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  onClick: () => void;
  index: number;
}

const ChoiceOption = ({ title, label, description, icon: Icon, color, onClick, index }: ChoiceOptionProps) => {
  const colorMap: Record<string, string> = {
    amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    purple: "text-purple-500 bg-purple-500/10 border-purple-500/20"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index }}
    >
      <button
        onClick={onClick}
        className={cn(
          "w-full group relative flex items-center gap-4 p-4 rounded-2xl border border-border/60 bg-background hover:bg-muted/50 transition-all duration-200 text-left",
          "hover:border-primary/20 hover:shadow-sm active:scale-[0.99]"
        )}
      >
        <div className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border",
          colorMap[color]
        )}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary/70">
              {title}
            </span>
          </div>
          <h3 className="text-base font-semibold text-foreground tracking-tight">
            {label}
          </h3>
          <p className="text-xs text-muted-foreground line-clamp-1 group-hover:text-foreground/70 transition-colors">
            {description}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-primary transition-colors" />
      </button>
    </motion.div>
  );
};

interface WelcomeChoiceStepProps {
  onPlan: () => void;
  onWrite: () => void;
  onStartingOut: () => void;
  onExit: () => void;
}

export function WelcomeChoiceStep({ onPlan, onWrite, onStartingOut, onExit }: WelcomeChoiceStepProps) {
  const choices = [
    { id: 'plan', title: "", label: "I plan before I write", description: "Outlines, beats, and structure first.", icon: Layout, color: "amber", onClick: onPlan },
    { id: 'write', title: "", label: "I write and figure it out", description: "Characters first, story follows.", icon: PencilLine, color: "emerald", onClick: onWrite },
    { id: 'start', title: "", label: "I'm just starting out", description: "We'll guide you through everything, step by step.", icon: Sprout, color: "purple", onClick: onStartingOut }
  ];

  return (
    <div className="w-full max-w-xl mx-auto px-6 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground font-lora">
            How do you usually write?
          </h1>
          <p className="text-muted-foreground text-sm">
            We'll set up your workspace to match your process.
          </p>
        </div>
        <button onClick={onExit} className="p-2 hover:bg-muted/50 rounded-full text-muted-foreground/40 hover:text-foreground transition-all">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Choices List */}
      <div className="grid grid-cols-1 gap-3">
        {choices.map((choice, index) => (
          <ChoiceOption key={choice.id} {...choice} index={index} />
        ))}
      </div>

      {/* Footer Info */}
      <div className="text-center pt-2">
        <p className="inline-flex items-center gap-2 text-[11px] text-muted-foreground/60 font-medium px-4 py-1.5 rounded-full bg-muted/30 border border-border/40">
          <Sparkles size={12} className="text-amber-500/50" />
          You can customize these settings later in your dashboard.
        </p>
      </div>
    </div>
  );
}
